import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { schema } from "./validations";
import {
  DESCRIPTION,
  UPDATED_BY,
  ACTIVE,
  DOCUMENT_TYPE_ID,
  START_RANGE,
  TRANSACTIONAL_PROCESSOR,
  DOCUMENT_TYPE,
  END_RANGE,
  UPDATED_AT,
  COMMUNITY_ID,
  START_NUMBER,
  END_NUMBER,
  RANGE_DESCRIPTION,
  ACTIVATE_IMMEDIATELY,
  WARNING_PERCENTAGE,
} from "./consts";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useAddDocumentNumberRangeMutation,
  useAddDocumentTypeMutation,
  useGetDocumentNumberRanges,
  useGetDocumentTypeById,
  useUpdateDocumentTypeMutation,
} from "@/services";
import { useEffect, useState } from "react";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";

const useAddEditConfig = () => {
  const { id, documentTypeId, action } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isAdd = !documentTypeId;
  const isView = documentTypeId && action == "view";
  const [keys, setKeys] = useState({
    documentType: Math.random() * 3,
    processor: Math.random() * 3,
  });
  const [localNumberRanges, setLocalNumberRanges] = useState<any[]>([]);

  const form = useForm<any>({
    defaultValues: {
      [DOCUMENT_TYPE_ID]: "",
      [DOCUMENT_TYPE]: "",
      [TRANSACTIONAL_PROCESSOR]: "",
      [ACTIVE]: true,
      [START_RANGE]: "",
      [END_RANGE]: "",
      [DESCRIPTION]: "",
      [UPDATED_AT]: "",
      [UPDATED_BY]: "",
      [COMMUNITY_ID]: id,
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const isMasterData = location?.pathname?.toLowerCase()?.includes("master-data");
  const communityId = isMasterData ? communityDetail?.communityId : id;
  const documentTypeListPath = isMasterData
    ? `/${routes.admin}/${routes.masterData}/${routes.documentTypeConfig}`
    : `/${routes.admin}/${routes.adminConsole}/${routes.communities}/${id}/${routes.documentTypeConfig}`;

  const breadcrumbData = [
    ...(isMasterData
      ? [
          {
            link: `/${routes.admin}/${routes.masterData}`,
            name: "Master Data",
          },
          {
            link: "",
            name: <span>{"Document Type Config"}</span>,
          },
        ]
      : [
          {
            link: `/${routes.admin}/${routes.adminConsole}/${routes.communities}`,
            name: `Community - ${(communityDetail as any)?.name || ""}`,
          },
        ]),
    {
      link: documentTypeListPath,
      name: <span>{"Document Types"}</span>,
    },
    {
      link: "",
      name: (
        <span className="text-primary">
          {isAdd
            ? "Add Document Types"
            : isView
            ? "View Document Type"
            : "Edit Document Type"}
        </span>
      ),
    },
  ];

  const { mutateAsync: addInfo, isPending: isAddPending } =
    useAddDocumentTypeMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
    });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateDocumentTypeMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["document-type", communityId],
        });
        onBackClick();
        toast.success("Document Type Updated Successfully");
      },
    });

  const { data: docTypeDetails, isLoading: isDetailLoading } =
    useGetDocumentTypeById({
      id: documentTypeId as string,
      queryParams: {
        enabled: !!documentTypeId,
      },
    });

  const { data: numberRangeList, isLoading: isNumberRangeLoading } =
    useGetDocumentNumberRanges({
      documentTypeId: documentTypeId as string,
      queryParams: {
        enabled: !!documentTypeId,
      },
    });

  useEffect(() => {
    if (docTypeDetails) {
      form.reset({
        [DOCUMENT_TYPE_ID]:
          docTypeDetails?.[DOCUMENT_TYPE_ID]?.toString() || "",
        [DOCUMENT_TYPE]: docTypeDetails?.[DOCUMENT_TYPE] || "",
        [TRANSACTIONAL_PROCESSOR]:
          docTypeDetails?.[TRANSACTIONAL_PROCESSOR] || "",
        [ACTIVE]: docTypeDetails?.[ACTIVE] || true,
        [DESCRIPTION]: docTypeDetails?.[DESCRIPTION] || "",
        [UPDATED_AT]: docTypeDetails?.[UPDATED_AT]
          ? moment(docTypeDetails?.[UPDATED_AT]).format("MM/DD/YYYY")
          : "",
        [UPDATED_BY]: docTypeDetails?.updatedByUserName || docTypeDetails?.[UPDATED_BY] || "",
        [COMMUNITY_ID]: docTypeDetails?.[COMMUNITY_ID]?.toString() || "",
      });
    }
    setKeys({
      documentType: Math.random() * 2,
      processor: Math.random() * 2,
    });
  }, [docTypeDetails]);

  const numberRanges = documentTypeId
    ? (numberRangeList || [])
    : localNumberRanges;

  const { mutate: addNumberRange, mutateAsync: addNumberRangeAsync, isPending: isAddRangePending } =
    useAddDocumentNumberRangeMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
      },
      onSuccess: () => {
        if (!documentTypeId) return;
        queryClient.invalidateQueries({
          queryKey: ["document-number-ranges", documentTypeId],
        });
        toast.success("Number Range Added Successfully");
      },
    });

  const onAddRange = (data: any) => {
    if (documentTypeId) {
      addNumberRange({
        documentTypeId: documentTypeId as string,
        data: {
          [START_NUMBER]: data[START_NUMBER],
          [END_NUMBER]: data[END_NUMBER],
          [RANGE_DESCRIPTION]: data[RANGE_DESCRIPTION],
          [WARNING_PERCENTAGE]: data[WARNING_PERCENTAGE],
          [COMMUNITY_ID]: communityId,
          [ACTIVATE_IMMEDIATELY]: true,
        },
      });
      return;
    }

    setLocalNumberRanges((prev) => [
      ...prev.map((range) => ({ ...range, status: "INACTIVE", deactivatedAt: new Date().toISOString() })),
      {
        startNumber: Number(data[START_NUMBER]),
        endNumber: Number(data[END_NUMBER]),
        lastNumberUsed: "",
        nextNumber: Number(data[START_NUMBER]),
        status: "ACTIVE",
        description: data[RANGE_DESCRIPTION] || "",
        activatedAt: new Date().toISOString(),
        deactivatedAt: null,
      },
    ]);
  };

  const onSubmit = (data: any) => {
    const activeRange = numberRanges?.find((range: any) => String(range?.status || "").toUpperCase() === "ACTIVE") || numberRanges?.[0];
    let _data = removeEmptyFields({
      ...data,
      [START_RANGE]: activeRange?.startNumber || activeRange?.startRange || data[START_RANGE],
      [END_RANGE]: activeRange?.endNumber || activeRange?.endRange || data[END_RANGE],
    });
    _data = setEmptyFieldsToNull(_data);

    if (documentTypeId) {
      updateInfo({
        id: documentTypeId,
        data: {
          ..._data,
          [ACTIVE]: !!_data[ACTIVE],
          [COMMUNITY_ID]: communityId,
          [DOCUMENT_TYPE]: (_data[DOCUMENT_TYPE] as any)?.toUpperCase(),
          [TRANSACTIONAL_PROCESSOR]: (
            _data[TRANSACTIONAL_PROCESSOR] as any
          )?.toUpperCase(),
        },
      });
    } else {
      addInfo({
        data: {
          ..._data,
          [COMMUNITY_ID]: communityId,
          [DOCUMENT_TYPE]: (_data[DOCUMENT_TYPE] as any)?.toUpperCase(),
          [TRANSACTIONAL_PROCESSOR]: (
            _data[TRANSACTIONAL_PROCESSOR] as any
          )?.toUpperCase(),
        },
      }).then(async (response: any) => {
        const created = response?.data?.data || {};
        const newDocumentTypeId = created?.[DOCUMENT_TYPE_ID];
        if (newDocumentTypeId && localNumberRanges?.length) {
          try {
            for (const range of localNumberRanges) {
              await addNumberRangeAsync({
                documentTypeId: String(newDocumentTypeId),
                data: {
                  [START_NUMBER]: range.startNumber,
                  [END_NUMBER]: range.endNumber,
                  [RANGE_DESCRIPTION]: range.description,
                  [COMMUNITY_ID]: communityId,
                },
              });
            }
          } catch {
            // range error is already toasted
          }
        }
        queryClient.invalidateQueries({
          queryKey: ["document-type", communityId],
        });
        toast.success("Document Type Added Successfully");
        onBackClick();
      }).catch(() => undefined);
    }
  };

  const onBackClick = () => {
    navigate(documentTypeListPath);
  };

  return {
    form,
    onSubmit,
    isAdd,
    isDetailLoading: isDetailLoading || isNumberRangeLoading,
    breadcrumbData,
    isView,
    onBackClick,
    requiredFields,
    onCancel: onBackClick,
    isAddPending,
    isUpdatePending,
    documentTypeId,
    keys,
    numberRanges,
    onAddRange,
    isAddRangePending,
  };
};

export default useAddEditConfig;
